import { PAGE_SEO } from '@/data/pageSeoConfig';
import { LegalPageTemplate, MUTED, type LegalPageConfig } from './LegalPageTemplate';

const config: LegalPageConfig = {
  seo: PAGE_SEO.politykaZwrotow,
  breadcrumbLabel: 'Polityka zwrotów',
  heroTitle: 'Polityka zwrotów',
  heroSubtitle: (
    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px,1.4vw,15.5px)', color: MUTED, maxWidth: 520, margin: '0 auto', lineHeight: 1.75 }}>
      Informacje dotyczące prawa odstąpienia od umowy, wyjątków oraz zasad dokonywania zwrotów.
    </p>
  ),
  toc: [
    { id: 'prawo-odstapienia',       label: '1. Prawo odstąpienia od umowy' },
    { id: 'kiedy-zwrot-niemozliwy',  label: '2. Kiedy zwrot nie jest możliwy?' },
    { id: 'zwrot-produktow',         label: '3. Zwrot produktów' },
    { id: 'zwrot-platnosci',         label: '4. Zwrot płatności' },
    { id: 'koszt-zwrotu',            label: '5. Koszt zwrotu' },
    { id: 'zamowienia-indywidualne', label: '6. Produkty wykonane na indywidualne zamówienie' },
    { id: 'reklamacje',              label: '7. Reklamacje' },
    { id: 'kontakt',                 label: '8. Kontakt' },
  ],
  tocVariant: 'collapsible',
  articleSeparator: 'border',
  articles: [
    {
      id: 'prawo-odstapienia',
      title: '1. Prawo odstąpienia od umowy',
      body: [
        { type: 'paragraph', html: '<p>Konsument, który zawarł umowę na odległość, co do zasady ma prawo odstąpić od niej w terminie <strong data-hi="true">14 dni kalendarzowych</strong> bez podawania przyczyny.</p>' },
        { type: 'paragraph', html: '<p>Termin na odstąpienie od umowy rozpoczyna się od dnia, w którym konsument lub wskazana przez niego osoba trzecia, inna niż przewoźnik, objęła towar w posiadanie.</p>' },
        { type: 'paragraph', html: '<p>Oświadczenie o odstąpieniu od umowy można złożyć w formie pisemnej lub elektronicznej, korzystając z danych kontaktowych Sprzedawcy wskazanych na stronie internetowej.</p>' },
        { type: 'paragraph', html: '<p>Do zachowania terminu wystarczy wysłanie oświadczenia przed upływem <strong data-hi="true">14-dniowego terminu</strong>.</p>' },
      ],
    },
    {
      id: 'kiedy-zwrot-niemozliwy',
      title: '2. Kiedy zwrot nie jest możliwy?',
      body: [
        { type: 'paragraph', html: '<p>Prawo odstąpienia od umowy nie przysługuje w przypadkach przewidzianych w obowiązujących przepisach prawa.</p>' },
        { type: 'paragraph', html: '<p>W szczególności dotyczy to umów, których przedmiotem są towary:</p>' },
        { type: 'list', ordered: false, itemCount: 3, html: '<ul><li>ulegające szybkiemu zepsuciu lub mające krótki termin przydatności do użycia, w szczególności świeże kwiaty i kompozycje kwiatowe;</li><li>nieprefabrykowane, wykonane według specyfikacji konsumenta lub służące zaspokojeniu jego zindywidualizowanych potrzeb;</li><li>przygotowane na indywidualne zamówienie klienta, jeżeli charakter produktu uniemożliwia jego ponowne wprowadzenie do sprzedaży.</li></ul>' },
        { type: 'paragraph', html: '<p>W związku z charakterem oferowanych przez Bukietową produktów, w szczególności świeżych kwiatów i indywidualnie przygotowywanych kompozycji, możliwość odstąpienia od umowy może być wyłączona w przypadkach określonych przepisami prawa.</p>' },
      ],
    },
    {
      id: 'zwrot-produktow',
      title: '3. Zwrot produktów, dla których odstąpienie od umowy jest możliwe',
      body: [
        { type: 'paragraph', html: '<p>Jeżeli w danym przypadku konsumentowi przysługuje prawo odstąpienia od umowy, jest on zobowiązany zwrócić produkt niezwłocznie, nie później niż w terminie <strong data-hi="true">14 dni</strong> od dnia, w którym odstąpił od umowy.</p>' },
        { type: 'paragraph', html: '<p>Do zachowania terminu wystarczy odesłanie produktu przed jego upływem.</p>' },
        { type: 'paragraph', html: '<p>Produkt powinien zostać zwrócony w stanie pozwalającym na jego ponowną sprzedaż, z uwzględnieniem charakteru produktu oraz zakresu dozwolonego korzystania z niego w celu sprawdzenia jego charakteru, cech i funkcjonowania.</p>' },
      ],
    },
    {
      id: 'zwrot-platnosci',
      title: '4. Zwrot płatności',
      body: [
        { type: 'paragraph', html: '<p>W przypadku skutecznego odstąpienia od umowy Sprzedawca zwróci konsumentowi wszystkie otrzymane płatności, w tym koszty dostarczenia towaru, z zastrzeżeniem przypadków przewidzianych przepisami prawa.</p>' },
        { type: 'paragraph', html: '<p>Zwrot płatności nastąpi przy użyciu takiego samego sposobu płatności, jakiego użył konsument, chyba że konsument wyraźnie zgodził się na inne rozwiązanie, które nie wiąże się dla niego z dodatkowymi kosztami.</p>' },
        { type: 'paragraph', html: '<p>Sprzedawca może wstrzymać się ze zwrotem płatności do czasu otrzymania zwracanego produktu lub dostarczenia przez konsumenta dowodu jego odesłania – w zależności od tego, które zdarzenie nastąpi wcześniej.</p>' },
      ],
    },
    {
      id: 'koszt-zwrotu',
      title: '5. Koszt zwrotu',
      body: [
        { type: 'paragraph', html: '<p>W przypadku odstąpienia od umowy konsument ponosi bezpośrednie koszty zwrotu produktu, chyba że obowiązujące przepisy prawa stanowią inaczej.</p>' },
        { type: 'paragraph', html: '<p>Jeżeli konsument wybrał sposób dostawy inny niż najtańszy zwykły sposób dostawy oferowany przez Sprzedawcę, Sprzedawca nie jest zobowiązany do zwrotu dodatkowych kosztów wynikających z wyboru droższej formy dostawy.</p>' },
      ],
    },
    {
      id: 'zamowienia-indywidualne',
      title: '6. Produkty wykonane na indywidualne zamówienie',
      body: [
        { type: 'paragraph', html: '<p>W przypadku produktów przygotowywanych według indywidualnych wytycznych klienta, w tym spersonalizowanych kompozycji kwiatowych, możliwość odstąpienia od umowy może być wyłączona na zasadach określonych w obowiązujących przepisach prawa.</p>' },
        { type: 'paragraph', html: '<p>Dotyczy to w szczególności produktów nieprefabrykowanych, przygotowanych według specyfikacji konsumenta lub służących zaspokojeniu jego indywidualnych potrzeb.</p>' },
      ],
    },
    {
      id: 'reklamacje',
      title: '7. Reklamacje',
      body: [
        { type: 'paragraph', html: '<p>Prawo do odstąpienia od umowy jest niezależne od uprawnień konsumenta wynikających z niezgodności towaru z umową.</p>' },
        { type: 'paragraph', html: '<p>Jeżeli dostarczony produkt jest niezgodny z umową, konsument może skorzystać z uprawnień przysługujących mu na podstawie obowiązujących przepisów prawa.</p>' },
        { type: 'paragraph', html: '<p>Reklamację można złożyć drogą elektroniczną lub w innej formie umożliwiającej jej rozpatrzenie. W zgłoszeniu warto wskazać numer zamówienia, opis problemu oraz oczekiwany sposób rozwiązania sprawy.</p>' },
      ],
    },
    {
      id: 'kontakt',
      title: '8. Kontakt',
      body: [
        { type: 'paragraph', html: '<p>W sprawach dotyczących zwrotów, odstąpienia od umowy oraz reklamacji prosimy o kontakt z Bukietową za pośrednictwem danych kontaktowych dostępnych na stronie sklepu.</p>' },
        { type: 'paragraph', html: '<p class="legal-muted">Niniejsza polityka nie ogranicza praw konsumenta wynikających z bezwzględnie obowiązujących przepisów prawa.</p>' },
      ],
    },
  ],
};

export default function PolitykZwrotowPage() {
  return <LegalPageTemplate config={config} />;
}
